import { useRef } from "react";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Download, Copy, CalendarCheck } from "lucide-react";

const MONTHS_IT = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];

const fmtIt = (iso) => {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS_IT[d.getMonth()]} ${d.getFullYear()}`;
};

export const CouponTicket = ({ data, bookingUrl, discount }) => {
  const qrWrapRef = useRef(null);
  const isRes = !!(data.res_date && data.res_time);

  const drawCanvas = () => {
    const c = document.createElement("canvas");
    c.width = 1080;
    c.height = 1440;
    const ctx = c.getContext("2d");

    ctx.fillStyle = "#050505";
    ctx.fillRect(0, 0, c.width, c.height);

    ctx.strokeStyle = "#D4AF37";
    ctx.lineWidth = 3;
    ctx.strokeRect(40, 40, c.width - 80, c.height - 80);
    ctx.lineWidth = 1;
    ctx.strokeRect(56, 56, c.width - 112, c.height - 112);

    ctx.textAlign = "center";
    ctx.fillStyle = "#D4AF37";
    ctx.font = "600 74px 'Playfair Display', Georgia, serif";
    ctx.fillText("BAIANEIRA", c.width / 2, 190);
    ctx.fillStyle = "#F5F2E9";
    ctx.font = "300 24px 'Manrope', sans-serif";
    ctx.fillText("S U S H I   F U S I O N   ·   R O M A", c.width / 2, 236);

    if (isRes) {
      ctx.fillStyle = "#D4AF37";
      ctx.font = "italic 600 104px 'Playfair Display', Georgia, serif";
      ctx.fillText("PRENOTAZIONE", c.width / 2, 400);
      ctx.fillStyle = "#F5F2E9";
      ctx.font = "500 40px 'Manrope', sans-serif";
      ctx.fillText(`-${discount}% SUL MENÙ ALLA CARTA`, c.width / 2, 470);
      ctx.fillStyle = "#F3E5AB";
      ctx.font = "700 42px 'Manrope', sans-serif";
      ctx.fillText(
        `${fmtIt(data.res_date)} · ORE ${data.res_time} · ${Number(data.guests) === 1 ? "1 PERSONA" : `${data.guests} PERSONE`}`,
        c.width / 2,
        555
      );
    } else {
      ctx.fillStyle = "#D4AF37";
      ctx.font = "italic 600 210px 'Playfair Display', Georgia, serif";
      ctx.fillText(`-${discount}%`, c.width / 2, 480);
      ctx.fillStyle = "#F5F2E9";
      ctx.font = "500 34px 'Manrope', sans-serif";
      ctx.fillText("SUL MENÙ ALLA CARTA", c.width / 2, 545);
    }

    ctx.fillStyle = "#E0E0E0";
    ctx.font = "italic 34px 'Playfair Display', Georgia, serif";
    ctx.fillText("«Il tuo prossimo viaggio nel gusto inizia qui.»", c.width / 2, 640);

    const qrCanvas = qrWrapRef.current?.querySelector("canvas");
    if (qrCanvas) {
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(c.width / 2 - 130, 700, 260, 260);
      ctx.drawImage(qrCanvas, c.width / 2 - 110, 720, 220, 220);
    }

    ctx.setLineDash([10, 8]);
    ctx.strokeStyle = "#D4AF37";
    ctx.strokeRect(190, 1030, c.width - 380, 110);
    ctx.setLineDash([]);
    ctx.fillStyle = "#D4AF37";
    ctx.font = "700 52px 'Manrope', sans-serif";
    ctx.fillText(data.code, c.width / 2, 1100);

    ctx.fillStyle = "#F5F2E9";
    ctx.font = "400 30px 'Manrope', sans-serif";
    ctx.fillText(
      isRes ? "Valida solo per la data prenotata" : `Valido fino al: ${fmtIt(data.expires_at)}`,
      c.width / 2,
      1210
    );
    ctx.fillStyle = "#F3E5AB";
    ctx.font = "700 22px 'Manrope', sans-serif";
    ctx.fillText(
      isRes
        ? "PRENOTAZIONE CON 20% DI SCONTO · CONFERMA INVIATA VIA EMAIL"
        : "SCONTO VALIDO SOLO SCARICANDO IL COUPON E PRENOTANDO IL TAVOLO",
      c.width / 2,
      1264
    );
    ctx.fillStyle = "#A3A3A3";
    ctx.font = "400 22px 'Manrope', sans-serif";
    ctx.fillText("Bevande, dolci, coperto, aperisushi e menù degustazione esclusi. Consulta le condizioni.", c.width / 2, 1308);
    if (!isRes) {
      ctx.fillStyle = "#F3E5AB";
      ctx.font = "600 24px 'Manrope', sans-serif";
      ctx.fillText("Segnala il coupon al momento della prenotazione o del tuo arrivo.", c.width / 2, 1354);
    }

    return c;
  };

  const canvasToBlob = (canvas) =>
    new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("toBlob non riuscito"))), "image/png");
    });

  const downloadWithBlob = (blob) => {
    const filename = `baianeira-${isRes ? "prenotazione" : "coupon"}-${data.code}.png`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success(isRes ? "Prenotazione salvata come immagine." : "Coupon salvato come immagine.");
  };

  const savePhoto = async () => {
    let blob;
    try {
      blob = await canvasToBlob(drawCanvas());
    } catch {
      toast.error("Generazione immagine non riuscita.");
      return;
    }

    const file = new File([blob], `baianeira-${isRes ? "prenotazione" : "coupon"}-${data.code}.png`, { type: "image/png" });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: isRes ? "Prenotazione Baianeira" : "Coupon Baianeira",
          text: isRes
            ? `Prenotazione ${data.code}: -${discount}% sul menù alla carta, ${fmtIt(data.res_date)} ore ${data.res_time}`
            : `Coupon ${data.code}: -${discount}% sul menù alla carta`,
        });
        toast.success("Fatto! Se non l'hai ancora salvata, riapri il menu e scegli \"Salva immagine\".");
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
        // share non disponibile o fallito: si prosegue con il download
      }
    }

    try {
      downloadWithBlob(blob);
    } catch {
      toast.error("Download non riuscito: tieni premuto il coupon e salva l'immagine.");
    }
  };

  const download = () => savePhoto();

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(data.code);
      toast.success("Codice copiato negli appunti.");
    } catch {
      toast.error("Copia non riuscita.");
    }
  };

  return (
    <motion.div
      data-testid="coupon-ticket"
      initial={{ opacity: 0, scale: 0.85, rotateX: 12 }}
      animate={{ opacity: 1, scale: 1, rotateX: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="mx-auto max-w-md"
    >
      {data.restored ? (
        <p data-testid="coupon-restore-note" className="mb-6 text-center text-xs tracking-[0.15em] text-[#F3E5AB]">
          {isRes ? "PRENOTAZIONE SALVATA SU QUESTO TELEFONO — GUARDABILE ANCHE SENZA CONNESSIONE" : "COUPON SALVATO SU QUESTO TELEFONO — GUARDABILE ANCHE SENZA CONNESSIONE"}
        </p>
      ) : data.email_sent === false ? (
        <p data-testid="coupon-email-note" className="mb-6 text-center text-xs tracking-[0.15em] text-[#E0B75F]">
          SALVA LA FOTO QUI SOTTO: L'INVIO EMAIL È IN RITARDO
        </p>
      ) : (
        <p data-testid="coupon-email-note" className="mb-6 text-center text-xs tracking-[0.15em] text-[#8FBF9F]">
          {data.already_issued
            ? isRes
              ? "HAI GIÀ UNA PRENOTAZIONE PER QUESTA DATA — ECCOLA QUI"
              : "QUESTO INDIRIZZO HA GIÀ UN COUPON VALIDO — ECCOLO QUI"
            : isRes
              ? "EMAIL INVIATA CON LA COPIA DELLA TUA PRENOTAZIONE"
              : "EMAIL INVIATA CON LA COPIA DEL TUO COUPON"}
        </p>
      )}

      <div className="relative border border-[#D4AF37] bg-[#0A0A0A] p-2">
        <div className="relative border border-[#D4AF37]/50 px-6 py-10 text-center md:px-10">
          <span className="absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-[#050505]" />
          <span className="absolute -right-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-[#050505]" />

          <p className="font-display text-xl tracking-[0.3em] text-[#D4AF37]">BAIANEIRA</p>
          <p className="mt-1 text-[9px] tracking-[0.5em] text-[#A3A3A3]">SUSHI FUSION</p>

          {isRes ? (
            <>
              <p className="gold-shimmer mt-6 font-display text-4xl font-semibold italic md:text-5xl">
                PRENOTAZIONE
              </p>
              <p className="mt-2 text-xs font-semibold tracking-[0.3em] text-[#F5F2E9] md:text-sm">
                -{discount}% SUL MENÙ ALLA CARTA
              </p>
              <p
                data-testid="ticket-reservation-details"
                className="mt-4 inline-block border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-4 py-2.5 text-xs font-bold tracking-[0.1em] text-[#F3E5AB] md:text-sm"
              >
                {fmtIt(data.res_date)} · ORE {data.res_time} · {Number(data.guests) === 1 ? "1 PERSONA" : `${data.guests} PERSONE`}
              </p>
            </>
          ) : (
            <>
              <p className="gold-shimmer mt-6 font-display text-7xl font-semibold md:text-8xl">
                -{discount}%
              </p>
              <p className="mt-1 text-xs font-semibold tracking-[0.3em] text-[#F5F2E9] md:text-sm">
                SUL MENÙ ALLA CARTA
              </p>
            </>
          )}

          <p className="mt-5 font-display text-sm italic text-[#E0E0E0] md:text-base">
            «Il tuo prossimo viaggio nel gusto inizia qui.»
          </p>

          <div className="my-7 flex items-center gap-4">
            <span className="h-px flex-1 bg-[#D4AF37]/30" />
            <span className="text-[9px] tracking-[0.4em] text-[#A3A3A3]">
              {isRes ? "CODICE PRENOTAZIONE" : "CODICE COUPON"}
            </span>
            <span className="h-px flex-1 bg-[#D4AF37]/30" />
          </div>

          <p
            data-testid="coupon-code-text"
            className="mx-auto inline-block border border-dashed border-[#D4AF37] px-5 py-3 font-display text-2xl tracking-[0.2em] text-[#D4AF37] md:text-3xl"
          >
            {data.code}
          </p>

          <div ref={qrWrapRef} className="mt-7 flex justify-center">
            <div className="bg-white p-2.5">
              <QRCodeCanvas
                value={data.code}
                size={120}
                level="M"
                marginSize={1}
                bgColor="#FFFFFF"
                fgColor="#0A0A0A"
              />
            </div>
          </div>

          <p data-testid="coupon-expiry-text" className="mt-6 flex items-center justify-center gap-2 text-xs tracking-[0.15em] text-[#F5F2E9] md:text-sm">
            <CalendarCheck size={14} className="text-[#D4AF37]" />
            {isRes ? (
              <>PRENOTATA PER: <strong className="text-[#D4AF37]">{fmtIt(data.res_date)} · ORE {data.res_time}</strong></>
            ) : (
              <>VALIDO FINO AL: <strong className="text-[#D4AF37]">{fmtIt(data.expires_at)}</strong></>
            )}
          </p>
          <p className="mt-4 text-[11px] font-semibold leading-relaxed tracking-[0.05em] text-[#F3E5AB]">
            {isRes
              ? "Lo sconto si applica sul menù alla carta per la data prenotata: trovi la conferma anche nella tua email."
              : "Lo sconto è valido solo scaricando il coupon e prenotando il tuo tavolo."}
          </p>
          <p className="mt-3 text-[10px] leading-relaxed text-[#A3A3A3]">
            Bevande, dolci, coperto, aperisushi e menù degustazione esclusi.
            Consulta le condizioni della promozione.
          </p>
        </div>
      </div>

      <p className="mt-6 text-center text-[10px] leading-relaxed tracking-[0.1em] text-[#A3A3A3]">
        CONSIGLIO: SALVA SUBITO LA FOTO SUL TELEFONO — POTRAI MOSTRARLA ANCHE SE IL SITO NON FOSSE RAGGIUNGIBILE.
      </p>

      <div className="mt-4 grid gap-3">
        <button
          data-testid="coupon-download-button"
          onClick={download}
          className="gold-glow flex items-center justify-center gap-3 bg-[#D4AF37] py-4 text-xs font-bold tracking-[0.25em] text-[#050505] transition-colors duration-300 hover:bg-[#F3E5AB] active:scale-[0.98] md:text-sm"
        >
          <Download size={16} /> SALVA LA FOTO SUL TELEFONO
        </button>
        <button
          data-testid="coupon-copy-button"
          onClick={copyCode}
          className="flex items-center justify-center gap-3 border border-[#D4AF37]/60 py-4 text-xs font-bold tracking-[0.25em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37]/10 active:scale-[0.98] md:text-sm"
        >
          <Copy size={16} /> COPIA IL CODICE
        </button>
        {isRes && (
          <a
            data-testid="coupon-call-button"
            href="tel:+393282711049"
            className="flex items-center justify-center gap-3 border border-[#2E5C44] bg-[#103B2A]/40 py-4 text-xs font-bold tracking-[0.25em] text-[#F5F2E9] transition-colors duration-300 hover:bg-[#103B2A] active:scale-[0.98] md:text-sm"
          >
            <CalendarCheck size={16} /> CAMBIO PROGRAMMI? CHIAMA IL RISTORANTE
          </a>
        )}
        {!isRes && (
          <p
            data-testid="coupon-arrival-notice"
            className="border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-3 text-center text-[10px] font-semibold leading-relaxed tracking-[0.15em] text-[#F3E5AB]"
          >
            IMPORTANTE: AL MOMENTO DELLA PRENOTAZIONE O DEL TUO ARRIVO, COMUNICA AL PERSONALE CHE HAI IL COUPON
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default CouponTicket;
